import { ReactElement, useEffect, useState } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import { translate } from '@micro-apps/env';
import { IconPlus } from '../../icon';
import { useCAWContext } from '../CAW';
import Promo from './Promo';

interface Props {
	/**
	 * `inline` renders the desktop eCAW row variant (ghost button), `summary` the
	 * mobile dialog summary variant (text link).
	 */
	variant: 'inline' | 'summary';
	errorPromoCode?: boolean;
}

/**
 * Keeps the promo code field behind an "Add Promo Code" affordance so guests are
 * not prompted to go coupon hunting mid-search. The reveal is deliberately kept
 * in component state only: refreshing or navigating away collapses it again.
 */
function PromoCodeField({ variant, errorPromoCode }: Props): ReactElement<any> {
	const { planStore, isPPC } = useCAWContext();
	const hasPromoCode = !!planStore?.search.promoCode;
	const [isFieldVisible, setIsFieldVisible] = useState(hasPromoCode);
	const [focusOnReveal, setFocusOnReveal] = useState(false);

	// Reveal the field when a code reaches the store from outside the widget (query
	// string, restored search) or when a search failed because of the code, so it
	// can never be applied while hidden.
	useEffect(() => {
		if (hasPromoCode || errorPromoCode) {
			setIsFieldVisible(true);
		}
	}, [hasPromoCode, errorPromoCode]);

	// An invalid code sends focus to the input, which only exists once revealed.
	useEffect(() => {
		if (errorPromoCode) {
			setFocusOnReveal(true);
		}
	}, [errorPromoCode]);

	// The PPC widget keeps its own vertical layout and is out of scope here.
	if (isPPC || isFieldVisible) {
		return variant === 'summary' ? (
			<div className="Summary-field Promo-summary w-full flex flex-col justify-start items-start spacing-card-padding-y-s spacing-card-padding-x-xs border-b border-b-border-subtle spacing-stack-2xs">
				<div className="Summary-content">
					<label className="text-label text-text-secondary Summary-label" htmlFor="promoCode">
						{translate('Promo Code')}
					</label>
				</div>
				<Promo
					wrapperClassName="w-full"
					inputClassName="h-[50px]"
					errorPromoCode={errorPromoCode}
					autoFocus={focusOnReveal}
				/>
			</div>
		) : (
			<Promo errorPromoCode={errorPromoCode} autoFocus={focusOnReveal} />
		);
	}

	const reveal = () => {
		setFocusOnReveal(true);
		setIsFieldVisible(true);
	};

	return (
		<div
			className={classnames('AddPromoCode-container flex', {
				'w-auto shrink-0 items-end': variant === 'inline',
				'Summary-field w-full spacing-card-padding-y-s spacing-card-padding-x-xs':
					variant === 'summary',
			})}
		>
			<button
				type="button"
				className={classnames(
					'AddPromoCode-button text-button2 text-text-primary flex items-center whitespace-nowrap spacing-button-inline-icon p-0 transition-colors hover:text-text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-strong [&::after]:hidden',
					{
						'h-10': variant === 'inline',
						'underline underline-offset-[5px]': variant === 'summary',
					}
				)}
				aria-expanded="false"
				aria-controls="promoCode"
				onClick={reveal}
			>
				<IconPlus width={10} height={10} fill="currentColor" />
				<span>{translate('Add Promo Code')}</span>
			</button>
		</div>
	);
}

export default observer(PromoCodeField);
